import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { EventType, HoursComputedInterface, PaymentStatus, RequestRecord } from '../../../models/request-round/request-record.interface';
import { RequestRecordService } from 'src/app/requests-handling/request-round/request-record-service/request-record.service';
import { catchError, map, of, Subject, takeUntil } from 'rxjs';
import { ToastService } from 'src/app/shared/services/toast-service/toast.service';
import { RequestMessagesTemplatingService } from 'src/app/requests-handling/request-messages-templating/request-messages-templating.service';
import { CaregiverRoundDTO } from 'src/app/shared/models/caregiver.model';

@Component({
  selector: 'app-request-record-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './request-record-form.component.html',
  styleUrl: './request-record-form.component.scss'
})
export class RequestRecordFormComponent implements OnDestroy, OnInit {

  private _hoursComputed!: HoursComputedInterface

  @Input() record!: RequestRecord;
  @Input() requestId!: number;
  @Input()
  set hoursComputed(value: HoursComputedInterface) {
    if (value) {
      this._hoursComputed = value;
      //TO UPDATE IN FUTURE RELEASES
      // this.hoursValidator = value.totalhourAmount - value.totalHoursWorked
      this.hoursValidator = value.totalhourAmount
    }
  }
  get hoursComputed(): HoursComputedInterface {
    return this._hoursComputed;
  }
  @Input() businessLine!: string;
  @Input() caregiverDTO!: CaregiverRoundDTO;
  @Input() isNew = false;
  @Output() onUpdate = new EventEmitter<void>();
  @Output() onDelete = new EventEmitter<void>();

  private unsubscribe$ = new Subject<void>();
  paymentURL!: string;
  caregiverList: CaregiverRoundDTO[] = []
  selectedCaregiver!: CaregiverRoundDTO | null;
  hoursValidator!: number;

  newRecordForm = new FormGroup({
    id: new UntypedFormControl(null),
    requestId: new UntypedFormControl(null, [Validators.required]),
    startDate: new UntypedFormControl(null, [Validators.required]),
    endDate: new UntypedFormControl(null, [Validators.required]),
    eventType: new UntypedFormControl(null, [Validators.required]),
    caregiverId: new UntypedFormControl(null),
    caregiverName: new UntypedFormControl(null),
    caregiverType: new UntypedFormControl(null, [Validators.required]),
    hoursWorked: new UntypedFormControl(0, [Validators.required, Validators.min(0)]),
    paymentStatus: new UntypedFormControl(null),
    paymentLink: new UntypedFormControl(null),
    discounted: new UntypedFormControl(false),
    sessionAmount: new UntypedFormControl(0, [Validators.required, Validators.min(0)]),
    notes: new UntypedFormControl(null),
    createdAt: new UntypedFormControl(null),
    updatedAt: new UntypedFormControl(null)
  })

  constructor(private recordService: RequestRecordService, private toast: ToastService, private messageTemplatingService: RequestMessagesTemplatingService) { }

  ngOnInit(): void {
    this.initCaregiverOptions();
    this.initForm();

    this.eventType.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((type) => this.applyEventTypeRules(type));
    this.applyEventTypeRules(this.eventType.value);
  }

  get PaymentStatus() {
    return this.newRecordForm.get('paymentStatus') as UntypedFormControl;
  }

  get PaymentLink() {
    return this.newRecordForm.get('paymentLink') as UntypedFormControl;
  }

  get hoursWorked() {
    return this.newRecordForm.get('hoursWorked') as UntypedFormControl;
  }

  get discounted() {
    return this.newRecordForm.get('discounted') as UntypedFormControl;
  }

  get sessionAmount() {
    return this.newRecordForm.get('sessionAmount') as UntypedFormControl;
  }

  get eventType() {
    return this.newRecordForm.get('eventType') as UntypedFormControl;
  }

  get caregiverId() {
    return this.newRecordForm.get('caregiverId') as UntypedFormControl;
  }

  get isAbsence(): boolean {
    return this.eventType?.value === EventType.ABSENCE;
  }

  // Utilizzo in regime APL: stato di chiusura, si liquida tramite fattura
  // Synergie fuori dalla piattaforma -> nessun link di pagamento F+H generabile.
  get isAplContract(): boolean {
    return this.PaymentStatus?.value === PaymentStatus.CONTRATTO_APL;
  }

  initForm() {

    this.newRecordForm.setValue({
      id: this.record?.id || null,
      requestId: this.requestId,
      startDate: this.record?.startDate.split('T')[0] || null,
      endDate: this.record?.endDate.split('T')[0] || null,
      eventType: this.record?.eventType || null,
      caregiverId: this.record?.caregiverId ?? null,
      caregiverName: this.record?.caregiverName ?? null,
      caregiverType: this.record?.caregiverType ?? this.businessLine,
      hoursWorked: this.record?.hoursWorked || 0,
      paymentStatus: this.record?.paymentStatus || null,
      paymentLink: this.record?.paymentLink || null,
      discounted: this.record?.discounted || false,
      sessionAmount: this.record?.sessionAmount || 0,
      notes: this.record?.notes || null,
      createdAt: this.record?.createdAt || null,
      updatedAt: this.record?.updatedAt || null
    })
    // TO UPDATE IN FUTURE RELEASES
    // this.hoursValidator += this.record?.hoursWorked || 0;
  }

  /**
   * Regole per il tipo evento "Assenza Ingiustificata": importo azzerato e non
   * modificabile, nessuno sconto, stato non fatturabile (CANCELLED), caregiver
   * obbligatorio. Uscendo dal tipo si ripristina il comportamento normale.
   * La correttezza è comunque garantita dal backend: qui evitiamo solo che il
   * Family Tutor inserisca una combinazione non valida.
   */
  private applyEventTypeRules(type: EventType | string | null): void {
    const isAbsence = type === EventType.ABSENCE;

    if (isAbsence) {
      this.newRecordForm.patchValue(
        {
          sessionAmount: 0,
          discounted: false,
          paymentStatus: PaymentStatus.CANCELLED,
        },
        { emitEvent: false }
      );
    }

    this.caregiverId.setValidators(isAbsence ? [Validators.required] : []);
    this.caregiverId.updateValueAndValidity({ emitEvent: false });
  }

  /** Ultima barriera prima del salvataggio: nessun valore stale per le assenze. */
  private enforceAbsenceInvariants(body: any): void {
    if (body.eventType === EventType.ABSENCE) {
      body.sessionAmount = 0;
      body.discounted = false;
      body.paymentStatus = PaymentStatus.CANCELLED;
    }
  }

  initCaregiverOptions(): void {
    const fromDTO = this.caregiverDTO;
    const recordHasCaregiver = !!(this.record?.caregiverId && this.record?.caregiverName);

    const fromRecord: CaregiverRoundDTO | null = recordHasCaregiver
      ? {
        caregiverId: this.record!.caregiverId,
        caregiverName: this.record!.caregiverName
      }
      : null;

    // Costruzione caregiverList evitando duplicati
    const caregivers: CaregiverRoundDTO[] = [];

    if (fromDTO) {
      caregivers.push(fromDTO);
    }

    if (
      fromRecord &&
      (!fromDTO || fromDTO.caregiverId !== fromRecord.caregiverId)
    ) {
      caregivers.push(fromRecord);
    }

    this.caregiverList = caregivers;

    // Imposta il selezionato solo se esiste un record
    this.selectedCaregiver = fromRecord
      ? caregivers.find(c => c.caregiverId === fromRecord.caregiverId) ?? null
      : null;
  }


  onCaregiverSelect(caregiver: CaregiverRoundDTO | null): void {
    this.selectedCaregiver = caregiver;

    this.newRecordForm.patchValue({
      caregiverId: caregiver?.caregiverId ?? null,
      caregiverName: caregiver?.caregiverName ?? null
    });
  }

  saveRound() {
    if (this.newRecordForm.invalid) {
      this.newRecordForm.markAllAsTouched();
      return;
    }
    const startDate = new Date(this.newRecordForm.value.startDate);
    const endDate = new Date(this.newRecordForm.value.endDate);
    const createdDate = new Date()
    let body = {
      id: null,
      requestId: this.requestId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      eventType: this.newRecordForm.value.eventType,
      caregiverId: this.newRecordForm.value.caregiverId ?? 'NESSUN CAREGIVER',
      caregiverName: this.newRecordForm.value.caregiverName ?? 'NESSUN CAREGIVER',
      caregiverType: this.record?.caregiverType ?? this.businessLine ?? null,
      hoursWorked: this.newRecordForm.value.hoursWorked,
      paymentStatus: this.newRecordForm.value.paymentStatus,
      paymentLink: this.newRecordForm.value.paymentLink,
      discounted: this.newRecordForm.value.discounted,
      sessionAmount: this.newRecordForm.value.sessionAmount,
      notes: this.newRecordForm.value.notes,
      createdAt: createdDate.toISOString(),
      updatedAt: createdDate.toISOString(),
    }
    this.enforceAbsenceInvariants(body);
    this.recordService.createRecord(body).pipe(
      takeUntil(this.unsubscribe$),
      map((response) => {
        this.toast.setAction('Round creato con successo!', true)
      }),
      catchError(() => {
        this.toast.setAction("Errore durante la creazione del round, controllare i dati inseriti e riprovare!", false)
        return of()
      })
    ).subscribe(() => {
      this.initForm()
      this.onUpdate.emit()
    })
  }

  updateRound() {
    if (this.newRecordForm.invalid) {
      this.newRecordForm.markAllAsTouched();
      return;
    }
    const startDate = new Date(this.newRecordForm.value.startDate);
    const endDate = new Date(this.newRecordForm.value.endDate);
    const creationDate = new Date(this.newRecordForm.value.createdAt);
    const updateDate = new Date();
    let body = {
      id: this.newRecordForm.value.id,
      requestId: this.requestId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      eventType: this.newRecordForm.value.eventType,
      caregiverId: this.newRecordForm.value.caregiverId ?? 'NESSUN CAREGIVER',
      caregiverName: this.newRecordForm.value.caregiverName ?? 'NESSUN CAREGIVER',
      caregiverType: this.record?.caregiverType ?? this.businessLine,
      hoursWorked: this.newRecordForm.value.hoursWorked,
      paymentStatus: this.newRecordForm.value.paymentStatus,
      paymentLink: this.newRecordForm.value.paymentLink,
      discounted: this.newRecordForm.value.discounted,
      sessionAmount: this.newRecordForm.value.sessionAmount,
      notes: this.newRecordForm.value.notes,
      createdAt: creationDate.toISOString(),
      updatedAt: updateDate.toISOString(),
    }
    this.enforceAbsenceInvariants(body);
    this.recordService.updateRecord(this.record.id!, body).pipe(
      takeUntil(this.unsubscribe$),
      map((response) => {
        this.toast.setAction('Round aggiornato con successo!', true)
      }),
      catchError(() => {
        this.toast.setAction("Errore durante l'aggiornamento del round, controllare i dati inseriti e riprovare!", false)
        return of()
      })
    ).subscribe(() => this.onUpdate.emit())
  }

  savePaymentLink() {
    this.recordService.savePaymentRoundLink(this.record.id!, this.PaymentLink.value).pipe(
      takeUntil(this.unsubscribe$),
      map((response) => {
        this.record = response;
        this.initForm()
      })
    ).subscribe()
  }

  createPaymentLink() {
    this.messageTemplatingService.updatePaymentUrl().pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      let paymentUpdate = value
      if (paymentUpdate !== '') {
        this.paymentURL = paymentUpdate
        this.PaymentLink.setValue(this.paymentURL)
        this.toast.setAction('Link di pagamento creato con successo!', true)
      } else {
        this.toast.setAction('Errore durante la creazione del link di pagamento!', false)
      }
    })
  }

  copyToClipboard() {
    navigator.clipboard.writeText(this.paymentURL);
    this.toast.setAction('Link di pagamento copiato con successo!', true)
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
